import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { NgbDropdown, NgbDropdownModule, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { Select2Module } from 'ng-select2-component';
import { NgxMapboxGLModule } from 'ngx-mapbox-gl';
import { IConfig, NgxMaskModule } from 'ngx-mask';
import {
  LucideAngularModule,
  ArrowLeft, RefreshCw, Search, X, Settings, CircleHelp, MessageCircle,
} from 'lucide-angular';
import { InlineBannerModule } from './components/inline-banner/inline-banner.module';
import { environment } from 'src/environments/environment';
import { ChatBotComponent } from './common/chat-bot/chat-bot/chat-bot.component';
import { ErrorComponent } from './common/error/error.component';
import { HeaderComponent } from './common/header/header.component';
import { PrivacyPolicyComponent } from './common/privacy-policy/privacy-policy.component';
import { SideNavigationComponent } from './common/side-navigation/side-navigation.component';
import { SidebarComponent } from './common/sidebar/sidebar.component';
import { NewSideNavigationComponent } from './common/new-side-navigation/new-side-navigation.component';
import { NewSidebarComponent } from './common/new-sidebar/new-sidebar.component';
import { TermServicesComponent } from './common/term-services/term-services.component';
import { ButtonComponent } from './components/button/button.component';
import { ModalActionBtnsComponent } from './components/button/modal-action-btns/modal-action-btns.component';
import { ConditionsComponent } from './components/conditions/conditions.component';
import { BulkUpdateComponent } from './components/datatable/bulk-update/bulk-update.component';
import { ErrorComponent as BulkUpdateErrorComponent } from './components/datatable/bulk-update/error/error.component';
import { DatatableComponent } from './components/datatable/datatable.component';
import { FiltersComponent } from './components/datatable/filters/filters.component';
import { LoaderComponent } from './components/datatable/loader/loader.component';
import { DefaultValueChangeDirective } from './components/directives/default-value-change.directive';
import { NumbersOnlyInputDirective } from './components/directives/onlynumberinput.directive';
import { DragUploadComponent } from './components/drag-upload/drag-upload.component';
import { CheckboxComponent } from './components/form-fields/checkbox/checkbox.component';
import { FormFieldsComponent } from './components/form-fields/form-fields.component';
import { InputComponent } from './components/form-fields/input/input.component';
import { RadioComponent } from './components/form-fields/radio/radio.component';
import { SelectSearchScrollComponent } from './components/form-fields/select-search-scroll/select-search-scroll.component';
import { SelectComponent } from './components/form-fields/select/select.component';
import { TextareaComponent } from './components/form-fields/textarea/textarea.component';
import { IconsComponent } from './components/icons/icons.component';
import { MultiFieldSelectorComponent } from './components/multi-field-selector/multi-field-selector.component';
import { CalendarComponent } from './components/report/view/calendar/calendar.component';
import { UpsertEventComponent } from './components/report/view/calendar/upsert-event/upsert-event.component';
import { GanttComponent } from './components/report/view/gantt/gantt.component';
import { FilterMapComponent } from './components/report/view/map/filter-map/filter-map.component';
import { MapComponent } from './components/report/view/map/map.component';
import { UpsertLocationComponent } from './components/report/view/map/upsert-location/upsert-location.component';
import { EmailTemplateComponent } from './components/settings/email-template/email-template.component';
import { BreadcrumsComponent } from './components/sub-header/breadcrums/breadcrums.component';
import { ProgressComponent } from './components/sub-header/progress/progress.component';
import { SubHeaderComponent } from './components/sub-header/sub-header.component';
import { UpsertComponent } from './components/templates/upsert/upsert.component';
import { ViewComponent } from './components/templates/view/view.component';
import { AppCurrencyPipe } from './pipes/app-currency.pipe';
import { SafeHTMLPipe } from './pipes/safe-html.pipe';
import { ThSeperatorPipe } from './pipes/th-seperator.pipe';
export const options: Partial<IConfig> | (() => Partial<IConfig>) | null = null;
// import { InPlaceEditorAllModule } from '@syncfusion/ej2-angular-inplace-editor';
import { ActionsMenuComponent } from './common/actions-menu/actions-menu.component';
import { ColorPickerModule } from './components/color-picker/color-picker.module';
import { DatatableBulkEditComponent } from './components/datatable/datatable-bulk-edit/datatable-bulk-edit.component';
import { InlineEditorComponent } from './components/datatable/inline-editor/inline-editor.component';
import { DynamicTitleDirective } from './components/directives/dynamic-title.directive';
import { SliderComponent } from './components/datatable/slider/slider.component';
import { ColumnFilterComponent } from './components/datatable/column-filter/column-filter.component';
import { ActionButtonComponent } from './components/datatable/action-button/action-button.component';
import { ThousandSeperatorInputComponent } from './components/form-fields/thousand-seperator-input/thousand-seperator-input.component';
import { SelectorDropdownValuesComponent } from './components/form-fields/selector-dropdown-values/selector-dropdown-values.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { SrOnlyComponent } from './components/sr-only/sr-only.component';
import { ChildTableModule } from './components/child-table/child-table.module';
import { DatatableCellDirective } from './components/datatable/directives/datatable-cell.directive';
import { RowCheckboxDirective } from './components/datatable/directives/row-checkbox.directive';
import { ActionContainerDirective } from './components/datatable/directives/action-container.directive';
import { DatatablePaginationComponent } from './components/datatable/pagination/pagination.component';

@NgModule({
  declarations: [
    NumbersOnlyInputDirective,
    DatatableCellDirective,
    RowCheckboxDirective,
    ActionContainerDirective,
    InputComponent,
    FormFieldsComponent,
    ErrorComponent,
    HeaderComponent,
    SidebarComponent,
    NewSidebarComponent,
    NewSideNavigationComponent,
    DragUploadComponent,
    SelectComponent,
    BreadcrumsComponent,
    SubHeaderComponent,
    ProgressComponent,
    CheckboxComponent,
    TextareaComponent,
    RadioComponent,
    DatatableComponent,
    SafeHTMLPipe,
    ConditionsComponent,
    MultiFieldSelectorComponent,
    FiltersComponent,
    LoaderComponent,
    ButtonComponent,
    ModalActionBtnsComponent,
    SelectSearchScrollComponent,
    CalendarComponent,
    MapComponent,
    GanttComponent,
    AppCurrencyPipe,
    ThSeperatorPipe,
    PrivacyPolicyComponent,
    TermServicesComponent,
    UpsertEventComponent,
    UpsertLocationComponent,
    BulkUpdateComponent,
    BulkUpdateErrorComponent,
    DefaultValueChangeDirective,
    ChatBotComponent,
    SideNavigationComponent,
    UpsertComponent,
    ViewComponent,
    IconsComponent,
    FilterMapComponent,
    EmailTemplateComponent,
    DynamicTitleDirective,
    InlineEditorComponent,
    DatatableBulkEditComponent,
    ActionsMenuComponent,
    SliderComponent,
    ColumnFilterComponent,
    ActionButtonComponent,
    ThousandSeperatorInputComponent,
    SelectorDropdownValuesComponent,
    SrOnlyComponent,
    DatatablePaginationComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatInputModule,
    Select2Module,
    NgxDatatableModule,
    NgbDropdownModule,
    NgxMaskModule.forRoot(),
    MatTooltipModule,
    NgSelectModule,
    ColorPickerModule,
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
    NgxMapboxGLModule.withConfig({
      accessToken: environment.mapBoxToken,
    }),
    MatTabsModule,
    AngularEditorModule,
    NgbPopoverModule,
    DragDropModule,
    ChildTableModule,
    LucideAngularModule.pick({
      ArrowLeft, RefreshCw, Search, X, Settings, CircleHelp, MessageCircle,
    }),
    InlineBannerModule,
  ],
  exports: [
    InputComponent,
    NumbersOnlyInputDirective,
    ErrorComponent,
    HeaderComponent,
    SelectComponent,
    DragUploadComponent,
    CheckboxComponent,
    TextareaComponent,
    RadioComponent,
    Select2Module,
    DatatableComponent,
    SafeHTMLPipe,
    MatInputModule,
    MatFormFieldModule,
    ConditionsComponent,
    MultiFieldSelectorComponent,
    ButtonComponent,
    ModalActionBtnsComponent,
    SelectSearchScrollComponent,
    NgSelectModule,
    GanttComponent,
    CalendarComponent,
    MapComponent,
    AppCurrencyPipe,
    ThSeperatorPipe,
    PrivacyPolicyComponent,
    TermServicesComponent,
    UpsertEventComponent,
    MatIconModule,
    DefaultValueChangeDirective,
    AngularEditorModule,
    ChatBotComponent,
    IconsComponent,
    NgbDropdownModule,
    EmailTemplateComponent,
    DynamicTitleDirective,
    ColorPickerModule,
    NgbPopoverModule,
    ActionsMenuComponent,
    ThousandSeperatorInputComponent,
    SelectorDropdownValuesComponent,
    SrOnlyComponent,
    ChildTableModule,
    DatatablePaginationComponent,
    LucideAngularModule,
    InlineBannerModule,
  ],
  providers: [NgbDropdown],
})
export class SharedModule {}
